import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useAuditSearch } from '../api/hooks';
import type { AuditRow } from '../api/types';
import { PageHeader } from '../app/Shell';
import { Badge, Button, Card, DataTable, Drawer, Id, JsonView, KeyValues, QueryView, Section, fmtAgo, fmtDateTime, type Tone } from '../ui';
import { LoadMore } from './parts';

const actionTone = (action: string): Tone =>
  /suspend|ban|delete|revoke/.test(action) || action === 'user.status'
    ? 'danger'
    : /credits|coins|plus/.test(action)
      ? 'accent'
      : /flag|app_config|team/.test(action)
        ? 'warn'
        : /report|notify/.test(action)
          ? 'info'
          : 'neutral';

const FIELDS = [
  ['actor', '操作人', '電郵或 user ID'],
  ['action', '操作', '例如 user. 或 plus.grant'],
  ['target', '對象 ID', 'user / job / chapter ID'],
  ['q', '原因包含', '關鍵字'],
] as const;

/** Every back-office write with who did it and why, searchable on the server. */
export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const filter = Object.fromEntries(FIELDS.map(([k]) => [k, params.get(k) ?? undefined])) as Record<(typeof FIELDS)[number][0], string | undefined>;
  const [draft, setDraft] = useState<Record<string, string>>(() => Object.fromEntries(FIELDS.map(([k]) => [k, params.get(k) ?? ''])));
  const list = useAuditSearch(filter);
  const [selected, setSelected] = useState<AuditRow | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setParams(Object.fromEntries(Object.entries(draft).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()])));
  };

  return (
    <>
      <PageHeader title="審計日誌" description="後台每一次寫入：誰、做了甚麼、對象、原因、改動前後。最新的在前。" />
      <Card flush>
        <form className="filters" onSubmit={submit}>
          {FIELDS.map(([k, label, ph]) => (
            <label key={k}>
              {label}
              <input type="text" value={draft[k] ?? ''} placeholder={ph} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
            </label>
          ))}
          <Button type="submit" size="sm" variant="primary">
            搜尋
          </Button>
          {Object.values(filter).some(Boolean) && (
            <Button
              size="sm"
              onClick={() => {
                setDraft({});
                setParams({});
              }}
            >
              清除
            </Button>
          )}
        </form>
        <QueryView query={list} loading="載入審計記錄…">
          {() => (
            <>
              <DataTable pageSize={25}
                rows={list.rows}
                rowKey={(a) => a._id}
                onRowClick={setSelected}
                selectedKey={selected?._id ?? null}
                empty={Object.values(filter).some(Boolean) ? '沒有符合的記錄。' : '還沒有審計記錄。'}
                columns={[
                  { key: 'at', header: '時間', render: (a) => <span title={fmtDateTime(a.createdAt)} className="nowrap">{fmtAgo(a.createdAt)}</span> },
                  { key: 'action', header: '操作', render: (a) => <Badge tone={actionTone(a.action)}>{a.action}</Badge> },
                  { key: 'actor', header: '操作人', render: (a) => (a.actorEmail ? <span className="nowrap">{a.actorEmail}</span> : <Id value={a.actorId} />) },
                  { key: 'target', header: '對象', render: (a) => <span className="nowrap">{a.targetType} <Id value={a.targetId} /></span> },
                  { key: 'reason', header: '原因', render: (a) => <span className="truncate" style={{ display: 'block', maxWidth: 420 }} title={a.reason}>{a.reason || <span className="faint">—</span>}</span> },
                ]}
              />
              <LoadMore q={list} />
            </>
          )}
        </QueryView>
      </Card>
      <Drawer open={selected !== null} title="審計記錄" onClose={() => setSelected(null)}>
        {selected && (
          <>
            <KeyValues
              items={[
                ['時間', fmtDateTime(selected.createdAt)],
                ['操作', <Badge tone={actionTone(selected.action)}>{selected.action}</Badge>],
                ['操作人', selected.actorEmail ? `${selected.actorEmail}（${selected.actorId}）` : <code>{selected.actorId}</code>],
                ['對象', selected.targetType === 'user' ? <Link to={`/users/${selected.targetId}`}>user · {selected.targetId}</Link> : <span>{selected.targetType} · <code>{selected.targetId}</code></span>],
                ['記錄 ID', <code>{selected._id}</code>],
              ]}
            />
            <Section title="原因">{selected.reason ? <p style={{ whiteSpace: 'pre-wrap' }}>{selected.reason}</p> : <p className="faint">—</p>}</Section>
            <Section title="改動前">
              <JsonView value={selected.before} />
            </Section>
            <Section title="改動後">
              <JsonView value={selected.after} />
            </Section>
          </>
        )}
      </Drawer>
    </>
  );
}
